import {
  PROBLEM_CODES,
  errorMessage,
  insufficientFunds,
  isProblemError,
  problemCode,
} from "../api";
import { Alert } from "../components";
import { formatTokens, subAmounts } from "../format";
import styles from "./AchatPage.module.css";

type Diagnostic = {
  titre: string;
  texte: string;
  /** Vrai pour une panne (5xx, réseau) ; faux pour un refus métier explicable. */
  grave: boolean;
};

/**
 * Traduit une erreur en message utile.
 *
 * On branche sur `problemCode(err)` — le jeton machine — et **jamais** sur le
 * texte : `detail` est rédigé côté serveur pour être lu, il peut être reformulé
 * ou traduit à tout moment, et un `includes("insuffisant")` casserait ce
 * jour-là sans que personne ne le voie.
 */
function diagnostiquer(erreur: unknown): Diagnostic {
  const manque = insufficientFunds(erreur);

  switch (problemCode(erreur)) {
    case PROBLEM_CODES.walletNotFound:
      return {
        titre: "Portefeuille introuvable",
        texte:
          "Le serveur ne connaît pas ce portefeuille : il n'y a rien à créditer. " +
          "La base est encore vide : le jeu de démonstration n'a pas été chargé. " +
          "Aucun paiement n'a été enregistré.",
        grave: false,
      };

    case PROBLEM_CODES.packNotFound:
      return {
        titre: "Pack introuvable",
        texte:
          "Ce pack ne fait plus partie du catalogue. Rechargez la liste, " +
          "puis choisissez-en un autre. Rien n'a été débité.",
        grave: false,
      };

    case PROBLEM_CODES.insufficientFunds:
      return {
        titre: "Solde insuffisant",
        texte:
          manque === null
            ? errorMessage(erreur)
            : `Il manque ${formatTokens(
                subAmounts(manque.requested, manque.available),
              )} tokens sur le seau demandé. Rien n'a été débité.`,
        grave: false,
      };

    case PROBLEM_CODES.paymentMethodRequired:
      return {
        titre: "Moyen de paiement requis",
        texte:
          "Un achat se règle par prélèvement : signez d'abord un mandat dans Paramètres → Paiement. Rien n'a été prélevé.",
        grave: false,
      };

    case PROBLEM_CODES.billingSuspended:
      return {
        titre: "Achats suspendus",
        texte:
          "Un prélèvement est resté impayé. Réglez sa facture, ou demandez à votre ESN d'enregistrer un achat réglé par virement.",
        grave: false,
      };

    case PROBLEM_CODES.paymentDeclined:
      return {
        titre: "Prélèvement refusé",
        texte: "Votre moyen de paiement a refusé le prélèvement. Rien n'a été crédité.",
        grave: false,
      };

    case PROBLEM_CODES.paymentPending:
      return {
        titre: "Prélèvement en attente",
        texte:
          "Le prestataire de paiement n'a pas répondu. Le prélèvement est enregistré : réessayez, la même clé ne prélèvera pas deux fois.",
        grave: true,
      };

    case PROBLEM_CODES.idempotencyKeyRequired:
    case PROBLEM_CODES.invalidHeader:
      return {
        titre: "Clé d'idempotence refusée",
        texte:
          "L'API exige une clé d'idempotence sur un achat et n'a pas pu lire la nôtre. " +
          "Elle préfère refuser plutôt que risquer un double crédit. " +
          "Rechargez la page pour repartir d'une clé neuve.",
        grave: true,
      };

    case PROBLEM_CODES.invalidPathParameter:
    case PROBLEM_CODES.invalidId:
    case PROBLEM_CODES.walletRequired:
      return {
        titre: "Identifiant invalide",
        texte:
          "L'identifiant de portefeuille ou de pack envoyé n'a pas la forme attendue. " +
          "Rechargez la page ; si le refus persiste, communiquez la référence au support.",
        grave: true,
      };

    case PROBLEM_CODES.internalError:
    case PROBLEM_CODES.dependencyUnavailable:
    case PROBLEM_CODES.timeout:
      return {
        titre: "Le service n'a pas pu répondre",
        texte:
          "L'achat n'a peut-être pas abouti, impossible de le savoir d'ici. " +
          "Réessayez : la tentative repart avec la même clé d'idempotence, " +
          "donc un achat déjà enregistré rendra son reçu au lieu d'être compté deux fois.",
        grave: true,
      };

    // `null` : ce n'est pas un refus du serveur mais une panne de transport.
    // L'achat a pu partir et sa réponse se perdre : c'est exactement le cas que
    // la clé d'idempotence couvre, et il faut le dire.
    case null:
      return {
        titre: "Service injoignable",
        texte:
          errorMessage(erreur) +
          " Si l'achat était parti, le réessayer avec la même clé ne créditera pas deux fois.",
        grave: true,
      };

    default:
      return {
        titre: "Achat refusé",
        texte: errorMessage(erreur),
        grave: isProblemError(erreur) && erreur.status >= 500,
      };
  }
}

export type AchatRefusProps = {
  /** L'erreur du dernier essai. Jamais `null` quand ce bloc est rendu. */
  erreur: unknown;
};

/**
 * Le refus, écrit en clair, à côté du bouton qui l'a provoqué.
 *
 * Un bloc en place plutôt qu'une fenêtre modale : la modale oblige à la fermer
 * avant de corriger, et fait perdre de vue le récapitulatif qu'on vient de lire.
 * La secousse de la carte (`useShake`) attire l'œil ici.
 */
export function AchatRefus({ erreur }: AchatRefusProps) {
  const { titre, texte, grave } = diagnostiquer(erreur);
  const reference = isProblemError(erreur) ? erreur.requestId : undefined;

  return (
    // `role="alert"` : le refus est annoncé dès son apparition, sans attendre
    // que l'utilisateur revienne dessus au clavier.
    <Alert tone={grave ? "danger" : "warning"} role="alert" title={titre}>
      {texte}
      {reference !== undefined && (
        <span className={styles.refusReference}>
          Référence à communiquer au support : {reference}
        </span>
      )}
    </Alert>
  );
}
